"use client";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/** Thin L→R shimmer bar — use while forms or actions are in flight */
export function FormAnticipationBar({
  show,
  label = "Loading",
  className,
}: {
  show: boolean;
  label?: string;
  className?: string;
}) {
  if (!show) return null;
  return (
    <div
      className={cn("h-1 w-full overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
      aria-label={label}
    >
      <div className="h-full w-full skeleton-shimmer" />
    </div>
  );
}

/** Card-shaped skeleton stack with a leading progress strip — full-page or panel loading */
export function BlockLoadingState({
  message,
  className,
  rows = 3,
}: {
  message?: string;
  className?: string;
  rows?: number;
}) {
  return (
    <div
      className={cn("flex w-full max-w-md flex-col gap-3", className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full w-1/3 skeleton-shimmer rounded-full" />
      </div>
      {message ? (
        <p className="text-center text-xs text-muted-foreground tabular-nums">{message}</p>
      ) : null}
      <div className="space-y-2 pt-1">
        {[...Array(rows)].map((_, i) => (
          <Skeleton
            key={i}
            className={cn("h-10 w-full", i === rows - 1 && "w-4/5")}
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

/** Vertically centered block — protected routes, onboarding, etc. */
export function FullPageLoadingState({
  message = "Loading…",
  className,
}: {
  message?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-screen w-full flex-col items-center justify-center bg-background px-4 py-16",
        className
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={message}
    >
      <BlockLoadingState message={message} rows={4} className="max-w-md w-full" />
    </div>
  );
}

/** Shorter vertical center — sections inside a layout that already has height */
export function SectionLoadingState({
  message,
  className,
  rows = 4,
}: {
  message?: string;
  className?: string;
  rows?: number;
}) {
  return (
    <div
      className={cn("flex w-full flex-col items-center justify-center py-12 px-4", className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <BlockLoadingState message={message} rows={rows} className="max-w-lg w-full" />
    </div>
  );
}

/** Sidebar / narrow column (asset browser, command palette results). */
export function CompactListSkeleton({
  items = 6,
  className,
}: {
  items?: number;
  className?: string;
}) {
  return (
    <div className={cn("w-full space-y-2 py-1", className)} role="status" aria-busy="true">
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full w-2/5 skeleton-shimmer rounded-full" />
      </div>
      {[...Array(items)].map((_, i) => (
        <Skeleton
          key={i}
          className="h-9 w-full rounded-md"
          style={{ animationDelay: `${i * 55}ms` }}
        />
      ))}
    </div>
  );
}
